/**
 * ApprovalCard — single pending-order tile with Approve/Reject buttons +
 * an auto-expire countdown. Used both in the PendingApprovals focal panel
 * and inline in the ChatDock when a tool result has status=pending_approval.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useAuth } from '../lib/auth';

interface Props {
  order: api.OrderPublic;
  onUpdate?: (updated: api.OrderPublic) => void;
  compact?: boolean;
}

export function ApprovalCard({ order, onUpdate, compact = false }: Props) {
  const { me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<api.OrderPublic | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Countdown
  useEffect(() => {
    if (!order.approval_expires_at) return;
    const target = new Date(order.approval_expires_at).getTime();
    const tick = () => {
      const now = Date.now();
      setSecondsLeft(Math.max(0, Math.round((target - now) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [order.approval_expires_at]);

  const isLive = order.mode === 'live';
  const expired = secondsLeft !== null && secondsLeft <= 0;

  async function approve() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.orders.approve(order.client_order_id ?? '', isLive);
      setDone(r.order);
      onUpdate?.(r.order);
    } catch (e: unknown) {
      setErr(safeError(e));
    } finally {
      setBusy(false);
    }
  }
  async function reject() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.orders.reject(order.client_order_id ?? '');
      setDone(r);
      onUpdate?.(r);
    } catch (e: unknown) {
      setErr(safeError(e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className={`card ${compact ? 'p-2' : 'p-3'} text-xs`}>
        <span className={done.status === 'filled' ? 'text-ok' : 'text-bad'}>
          ✓ {done.status === 'filled'
            ? `filled @ $${(done.fill_price ?? 0).toFixed(2)}`
            : `rejected: ${done.rejected_reason ?? ''}`}
        </span>
      </div>
    );
  }

  const cost = (order.fill_price ?? order.limit_price ?? 0) * order.quantity || 0;

  return (
    <div className={`card ${compact ? 'p-2' : 'p-3'} ${isLive ? 'border-bad' : ''}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-xs">
          <span className={isLive ? 'pill-live' : 'pill-sim'}>
            {order.mode.toUpperCase()}
          </span>
          <span
            className={`ml-2 num font-semibold ${
              order.side === 'buy' ? 'text-ok' : 'text-bad'
            }`}
          >
            {order.side.toUpperCase()} {order.quantity} {order.symbol}
          </span>
          <span className="ml-2 text-ink-mute num">
            {order.order_type === 'limit' && order.limit_price
              ? `@ $${order.limit_price.toFixed(2)} limit`
              : 'market'}
          </span>
          {cost > 0 && (
            <span className="ml-2 text-ink-mute num">~${cost.toFixed(2)}</span>
          )}
        </div>
        {secondsLeft !== null && !expired && (
          <span className="text-[10px] num text-ink-dim">{secondsLeft}s</span>
        )}
      </div>
      {err && <div className="text-bad text-xs mb-2">{err}</div>}
      {expired ? (
        <div className="text-warn text-xs">expired — place a new order if you still want it.</div>
      ) : (
        <div className="flex gap-2">
          <button
            className={isLive ? 'btn-danger text-xs' : 'btn-primary text-xs'}
            onClick={approve}
            disabled={busy || !me}
          >
            {busy ? '…' : isLive ? 'approve LIVE' : 'approve'}
          </button>
          <button className="btn text-xs" onClick={reject} disabled={busy}>
            reject
          </button>
        </div>
      )}
    </div>
  );
}

function safeError(e: unknown): string {
  const ae = e as { detail?: unknown; message?: string };
  if (ae?.detail && typeof ae.detail === 'object') {
    const d = ae.detail as Record<string, unknown>;
    if (d.detail && typeof d.detail === 'object') {
      const inner = d.detail as Record<string, unknown>;
      const reason = String(inner.reason ?? '');
      if (reason === 'live_ack_missing') return 'live ack header missing';
      if (reason === 'approval_expired') return 'expired';
      return reason;
    }
  }
  return ae?.message ?? String(e);
}
